/* eslint-disable no-undef */
/**
 * @module Components.Filter
 *
 */
import React, { useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";

import styles from "./styles.module.scss";
import { cls } from "app/utils";
import SpacesSelect from "./SpacesSelect";
import GroupsSelect from "./GroupsSelect";
import ListsSelect from "./ListsSelect";
import Search from "./Search";
import moment from "moment";
import { useTranslation } from "react-i18next";
import i18n from "app/i18n";
import { TeamModel } from "app/models";
import { RootState } from "typedefs";

type PropsDef = {
  initialTargetId?: string | null;
  selectedCalendarDate: string;
  onSelectedCalendarDateRemove: () => void;
  onSearchSubmit: (value: string) => void;
  onReset: (value: any) => void;
  onSubmit: (value: any, objectTypes?: any) => void;
};

// Finds a group, or a channel inside one, by id.
const findGroupOrChannel = (teams: TeamModel[], id: string) => {
  for (const team of teams) {
    if (team.id === id) {
      return team;
    }
    const channel = (team.teamGroups || []).find(
      (_channel) => _channel.id === id,
    );
    if (channel) {
      return channel;
    }
  }
  return null;
};

/**
 * @class ScheduleFilter
 *
 */
const ScheduleFilter = (props: PropsDef) => {
  const { t } = useTranslation();
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [clearSearchInput, setClearSearchInput] = useState(false);
  // Filled by the Groups dropdown's own fetch (teams with their channels).
  const teams = useSelector(
    (state: RootState) => state.team.data,
    shallowEqual,
  );
  // Also set once the user changes the filter, so a late teams fetch doesn't
  // bring the initial target back.
  const initialTargetShown = useRef(false);

  // The page was opened from a group or channel feed: show that group or
  // channel as selected, matching the list the page already filtered to it.
  useEffect(() => {
    if (!props.initialTargetId || initialTargetShown.current || !teams) {
      return;
    }
    const target = findGroupOrChannel(teams, props.initialTargetId);
    if (target) {
      initialTargetShown.current = true;
      setSelectedTargets([target]);
    }
  }, [teams, props.initialTargetId]);

  const onReset = (data: any) => {
    initialTargetShown.current = true;
    setSelectedTargets([]);
    setClearSearchInput(true);

    props.onReset(data);
  };

  const onSelectedCalendarDateRemove = () => {
    props.onSelectedCalendarDateRemove();
  };

  const onSearchSubmit = (value: string) => {
    setClearSearchInput(false);

    props.onSearchSubmit(value);
  };

  const onSelectedTargetRemove = (target: any) => {
    const remainedTargets = selectedTargets.filter(
      (_target: any) => _target.id !== target.id,
    );

    onSubmit(remainedTargets, null);
  };

  const onSubmit = (data: any, objectTypes?: any) => {
    initialTargetShown.current = true;
    // Refersh the list of targets for given objectTypes
    if (objectTypes) {
      // Remove all objectTypes items from already selected targets
      const restTargets = selectedTargets.filter(
        (_target: any) => !objectTypes.includes(_target.object),
      );
      // Add new list of targets to selected targets
      data = [...new Set([...restTargets, ...data])];
      // Update selectedTargets state
      setSelectedTargets(data);
    }

    const scheduleTargetIds = Array.from(data, (_target: any) => _target.id);

    setSelectedTargets(data);
    props.onSubmit({ scheduleTargetIds });
  };

  const formatSelecteDate = () => {
    if (!props.selectedCalendarDate) {
      return undefined;
    }

    return moment(props.selectedCalendarDate)
      .locale(i18n.language)
      .format("MMM DD, YYYY");
  };

  const showTargetAvatar = (target: any) => {
    if (!target) {
      return undefined;
    }

    switch (target.object) {
      case "athlete_space":
        return <img src={target.avatar} className={styles.spaceAvatar} />;
      case "team":
        return (
          <span className={styles.iconWrapper}>
            <i className={cls("ico", "ico-team", styles.icon)}></i>
          </span>
        );
      case "team_group":
        return (
          <span className={styles.iconWrapper}>
            <i className={cls("ico", "ico-team", styles.icon)}></i>
          </span>
        );
      case "segment":
        return (
          <span className={styles.iconWrapper}>
            <i className={cls("ico", "ico-groups", styles.icon)}></i>
          </span>
        );
      default:
        return null;
    }
  };

  const showTargetName = (target: any) => {
    if (!target) {
      return undefined;
    }

    switch (target.object) {
      case "athlete_space":
        return <span>{target.athleteName}</span>;
      case "team":
        return (
          <span>
            {target.name} {t("General")}
          </span>
        );
      case "team_group":
        return (
          <span>
            {target.team.name} ({target.name})
          </span>
        );
      case "segment":
        return <span>{target.name}</span>;
      default:
        return null;
    }
  };

  return (
    <div>
      <div className={styles.filterOptionsContainer}>
        <div className={styles.filter_buttons_wrapper}>
          <button className={cls(styles.filterButton)} onClick={onReset}>
            <i className={cls("ico", "ico-time", styles.icon)}></i>
            <span className={styles.buttonCaption}>{t("All")}</span>
          </button>

          <SpacesSelect selectedSpaces={selectedTargets} onSubmit={onSubmit} />
          <GroupsSelect selectedTeams={selectedTargets} onSubmit={onSubmit} />
          <ListsSelect selectedLists={selectedTargets} onSubmit={onSubmit} />
        </div>
        <Search
          clearSearchInput={clearSearchInput}
          onSearchSubmit={onSearchSubmit}
        />
      </div>

      <div className={styles.filterSelectedContainer}>
        <div
          className={
            props.selectedCalendarDate
              ? styles.filterSelectedItem
              : styles.hidden
          }
        >
          <span className={styles.iconTimeWrapper}>
            <i className={cls("ico", "ico-time", styles.icon)}></i>
          </span>
          <span className={cls(styles.timeCaption)}>{formatSelecteDate()}</span>
          <i
            className={cls("ico", "ico-x", styles.iconClose)}
            onClick={onSelectedCalendarDateRemove}
          ></i>
        </div>

        {selectedTargets.map((target) => (
          <div className={styles.filterSelectedItem} key={target.id}>
            {showTargetAvatar(target)}

            <span className={cls(styles.caption)}>
              {showTargetName(target)}
            </span>

            <i
              className={cls("ico", "ico-x", styles.iconClose)}
              onClick={() => onSelectedTargetRemove(target)}
            ></i>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScheduleFilter;
